/**
 * Escalation Protocol for ICS Hierarchy
 *
 * Handles escalating reports from sector rooms to command room.
 * Sector leads can mark messages as important and forward them to command.
 */

import * as ed from '@noble/ed25519'
import { sha512 } from '@noble/hashes/sha512'
import {
  uint8ArrayToBase64,
  base64ToUint8Array,
} from '../crypto/identity'
import type {
  Message,
  EscalatedReport,
  EscalationPriority,
  HierarchyProof,
} from '../types'

// Configure ed25519 to use sha512
ed.etc.sha512Sync = (...m) => sha512(ed.etc.concatBytes(...m))

/**
 * Create an escalated report from a message
 */
export async function createEscalatedReport(
  originalMessage: Message,
  sourceRoomId: string,
  sourceRoomName: string,
  escalatedBy: string,
  escalatedByName: string,
  priority: EscalationPriority,
  privateKey: Uint8Array
): Promise<EscalatedReport> {
  const id = crypto.randomUUID()
  const timestamp = new Date()

  // Create the payload to sign
  const payload = {
    type: 'escalated_report',
    id,
    sourceRoomId,
    originalMessageId: originalMessage.id,
    priority,
    timestamp: timestamp.toISOString(),
  }

  const payloadBytes = new TextEncoder().encode(JSON.stringify(payload))
  const signature = await ed.signAsync(payloadBytes, privateKey)

  return {
    type: 'escalated_report',
    id,
    sourceRoomId,
    sourceRoomName,
    originalMessage,
    escalatedBy,
    escalatedByName,
    priority,
    timestamp,
    signature: uint8ArrayToBase64(signature),
    acknowledged: false,
  }
}

/**
 * Verify an escalated report signature
 */
export async function verifyEscalatedReport(
  report: EscalatedReport,
  publicKey: Uint8Array
): Promise<boolean> {
  try {
    const payload = {
      type: 'escalated_report',
      id: report.id,
      sourceRoomId: report.sourceRoomId,
      originalMessageId: report.originalMessage.id,
      priority: report.priority,
      timestamp: report.timestamp instanceof Date
        ? report.timestamp.toISOString()
        : report.timestamp,
    }

    const payloadBytes = new TextEncoder().encode(JSON.stringify(payload))
    const signature = base64ToUint8Array(report.signature)

    return await ed.verifyAsync(signature, payloadBytes, publicKey)
  } catch {
    return false
  }
}

/**
 * Acknowledge an escalated report
 */
export function acknowledgeReport(
  report: EscalatedReport,
  acknowledgedBy: string
): EscalatedReport {
  return {
    ...report,
    acknowledged: true,
    acknowledgedBy,
    acknowledgedAt: new Date(),
  }
}

/**
 * Serialize an escalated report for transmission
 */
export function serializeEscalatedReport(report: EscalatedReport): Uint8Array {
  const json = JSON.stringify({
    ...report,
    timestamp: report.timestamp instanceof Date
      ? report.timestamp.toISOString()
      : report.timestamp,
    acknowledgedAt: report.acknowledgedAt instanceof Date
      ? report.acknowledgedAt.toISOString()
      : report.acknowledgedAt,
    originalMessage: {
      ...report.originalMessage,
      timestamp: report.originalMessage.timestamp instanceof Date
        ? report.originalMessage.timestamp.toISOString()
        : report.originalMessage.timestamp,
    },
  })
  return new TextEncoder().encode(json)
}

/**
 * Deserialize an escalated report from transmission
 */
export function deserializeEscalatedReport(data: Uint8Array): EscalatedReport {
  const json = new TextDecoder().decode(data)
  const parsed = JSON.parse(json)

  return {
    ...parsed,
    timestamp: new Date(parsed.timestamp),
    acknowledgedAt: parsed.acknowledgedAt ? new Date(parsed.acknowledgedAt) : undefined,
    originalMessage: {
      ...parsed.originalMessage,
      timestamp: new Date(parsed.originalMessage.timestamp),
    },
  }
}

/**
 * Filter escalated reports by priority
 */
export function filterByPriority(
  reports: EscalatedReport[],
  minPriority: EscalationPriority
): EscalatedReport[] {
  const priorityOrder: Record<EscalationPriority, number> = {
    routine: 0,
    urgent: 1,
    critical: 2,
  }

  const minOrder = priorityOrder[minPriority]

  return reports.filter(
    (report) => priorityOrder[report.priority] >= minOrder
  )
}

/**
 * Sort escalated reports by priority (critical first) then by timestamp (newest first)
 */
export function sortEscalatedReports(reports: EscalatedReport[]): EscalatedReport[] {
  const priorityOrder: Record<EscalationPriority, number> = {
    critical: 2,
    urgent: 1,
    routine: 0,
  }

  return [...reports].sort((a, b) => {
    // First by priority (higher first)
    const priorityDiff = priorityOrder[b.priority] - priorityOrder[a.priority]
    if (priorityDiff !== 0) return priorityDiff

    // Then by timestamp (newer first)
    const timeA = a.timestamp instanceof Date ? a.timestamp.getTime() : new Date(a.timestamp).getTime()
    const timeB = b.timestamp instanceof Date ? b.timestamp.getTime() : new Date(b.timestamp).getTime()
    return timeB - timeA
  })
}

/**
 * Get unacknowledged reports
 */
export function getUnacknowledgedReports(
  reports: EscalatedReport[]
): EscalatedReport[] {
  return reports.filter((report) => !report.acknowledged)
}

/**
 * Group reports by source room
 */
export function groupBySourceRoom(
  reports: EscalatedReport[]
): Map<string, EscalatedReport[]> {
  const grouped = new Map<string, EscalatedReport[]>()

  for (const report of reports) {
    const existing = grouped.get(report.sourceRoomId) || []
    existing.push(report)
    grouped.set(report.sourceRoomId, existing)
  }

  return grouped
}

/**
 * Create hierarchy proof for a sector room
 * This proves the sector belongs to the command room
 */
export async function createHierarchyProof(
  commandRoomId: string,
  sectorRoomId: string,
  sectorName: string,
  commanderPrivateKey: Uint8Array,
  commanderPublicKey: Uint8Array
): Promise<HierarchyProof> {
  const creationTimestamp = new Date()

  const payload = {
    commandRoomId,
    sectorRoomId,
    sectorName,
    creationTimestamp: creationTimestamp.toISOString(),
  }

  const payloadBytes = new TextEncoder().encode(JSON.stringify(payload))
  const signature = await ed.signAsync(payloadBytes, commanderPrivateKey)

  return {
    commandRoomId,
    sectorRoomId,
    sectorName,
    sectorCreationTimestamp: creationTimestamp,
    commanderPublicKey: uint8ArrayToBase64(commanderPublicKey),
    signature: uint8ArrayToBase64(signature),
  }
}

/**
 * Verify a hierarchy proof
 */
export async function verifyHierarchyProof(
  proof: HierarchyProof
): Promise<boolean> {
  try {
    const payload = {
      commandRoomId: proof.commandRoomId,
      sectorRoomId: proof.sectorRoomId,
      sectorName: proof.sectorName,
      creationTimestamp: proof.sectorCreationTimestamp instanceof Date
        ? proof.sectorCreationTimestamp.toISOString()
        : proof.sectorCreationTimestamp,
    }

    const payloadBytes = new TextEncoder().encode(JSON.stringify(payload))
    const signature = base64ToUint8Array(proof.signature)
    const publicKey = base64ToUint8Array(proof.commanderPublicKey)

    return await ed.verifyAsync(signature, payloadBytes, publicKey)
  } catch {
    return false
  }
}

/**
 * Serialize hierarchy proof for transmission
 */
export function serializeHierarchyProof(proof: HierarchyProof): Uint8Array {
  const json = JSON.stringify({
    ...proof,
    sectorCreationTimestamp: proof.sectorCreationTimestamp instanceof Date
      ? proof.sectorCreationTimestamp.toISOString()
      : proof.sectorCreationTimestamp,
  })
  return new TextEncoder().encode(json)
}

/**
 * Deserialize hierarchy proof from transmission
 */
export function deserializeHierarchyProof(data: Uint8Array): HierarchyProof {
  const json = new TextDecoder().decode(data)
  const parsed = JSON.parse(json)

  return {
    ...parsed,
    sectorCreationTimestamp: new Date(parsed.sectorCreationTimestamp),
  }
}
